import React, { useState, useRef } from "react";
import "../styles/HomePage.css";

const specialFormats = new Set(["HL7", "EDI", "X12"]);

const ConvertProHomepage = () => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState("");
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const [isConverting, setIsConverting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState("");

  const formatOptions = {
    Documents: ["PDF", "DOCX", "TXT", "XLSX", "ODT", "EPUB", "RTF", "HTML", "PPTX" , "PPT" , "DOC" , "XLS" , "ODS" , "CSV" , "MD" , "LATEX" , "TEX"],
    Images: ["PNG", "JPG", "SVG", "WEBP", "GIF", "HEIC", "TIFF", "BMP", "ICO" , "AVIF" , "RAW" , "CR2" , "CR3" , "NEF" , "ARW" , "PSD" , "AI" , "EPS"],
    Video: ["MP4", "AVI", "MOV", "MKV", "WEBM", "FLV", "WMV", "3GP" , "MPEG" , "MPG" , "M4V" , "TS" , "VOB" , "OGV" , "ASF"],
    Audio: ["MP3", "WAV", "FLAC", "AAC", "OGG", "M4A", "WMA", "AIFF" , "AMR" , "OPUS" , "MID" , "MIDI" , "AC3" , "APE" , "AU"],
    Archives: ["ZIP", "RAR", "7Z", "TAR", "GZ", "BZ2" , "XZ" , "CAB" , "ISO" , "TGZ" , "TAR.GZ"],
    Data: ["CSV", "JSON", "XML", "YAML", "SQL", "TSV", "NDJSON" , "JSONL" , "TOML" , "INI" , "PROTOBUF","GraphQL"],
    Healthcare_Docs: ["HL7 v2.x","HL7 v3","CCD","CDA","FHIR", "X12 270" , "X12 271" , "X12 276" , "X12 277" , "X12 278" , "X12 835" , "X12 837" , "DICOM"],
    EDI_Docs: ["850 Purchase Order" , "810 Invoice" , "856 ASN" , "855 PO Acknowledgement" , "820 Payment" , "846 Inventory" , "940 Warehouse" , "945 Shipment" , "997 Acknowledgement"],
    Hadoop_Docs: ["Parquet","Avro","ORC","RC", "Delta" , "Iceberg" , "Hudi" , "Arrow" , "Feather"],
    GIS_Mapping: ["GeoJSON" , "KML" , "GPX" , "Shapefile" , "TopoJSON"],
    Scientific: ["HDF5" , "NetCDF" , "MAT" , "NPY" , "NPZ"],
    Cad_3d: ["STL" , "OBJ" , "FBX" , "GLTF" , "GLB" , "STEP" , "DWG" , "DXF"],
    Security: ["PEM" , "CRT" , "CER" , "PFX" , "P12" , "JKS"]
  };



  const getFileExtension = (filename) => {
    return filename.split(".").pop().toUpperCase();
  };

  const getFileIcon = (ext) => {
    const docExts = ["PDF", "DOCX", "TXT", "XLSX", "ODT", "EPUB", "RTF", "HTML", "DOC"];
    const imgExts = ["PNG", "JPG", "JPEG", "SVG", "WEBP", "GIF", "HEIC", "TIFF", "BMP"];
    const vidExts = ["MP4", "AVI", "MOV", "MKV", "WEBM", "FLV", "WMV"];
    const audExts = ["MP3", "WAV", "FLAC", "AAC", "OGG", "M4A", "WMA"];
    if (docExts.includes(ext)) return "file-text";
    if (imgExts.includes(ext)) return "image";
    if (vidExts.includes(ext)) return "video";
    if (audExts.includes(ext)) return "music";
    return "file";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setTargetFormat("");
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setTargetFormat("");
      setDownloadUrl(null);
      setError("");
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setTargetFormat("");
    setShowFormatDropdown(false);
    setDownloadUrl(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleConvert = async () => {

    if (!selectedFile || !targetFormat) return;

    try {

      setIsConverting(true);
      setError("");
      setDownloadUrl(null);

      const formData = new FormData();

      formData.append(
        "file",
        selectedFile
      );

      formData.append(
        "from_format",
        getFileExtension(
          selectedFile.name
        ).toLowerCase()
      );

      formData.append(
        "to_format",
        targetFormat.toLowerCase()
      );

      const currentUser = JSON.parse(
        localStorage.getItem(
          "currentUser"
        ) || "null"
      );

      if (currentUser) {

        formData.append(
          "user_id",
          currentUser.user_id
        );

      }

      const response = await fetch(
        `${API_URL}/convert`,
        {
          method: "POST",
          body: formData
        }
      );

      const data =
        await response.json();

      if (!response.ok) {

        throw new Error(
          data.detail ||
          data.error ||
          "Conversion failed"
        );

      }

      setDownloadUrl(
        data.download_url
      );

    } catch (err) {

      setError(
        err.message
      );

    } finally {

      setIsConverting(false);

    }

  };

  return (
    <div className="hp-page">
      {/* ── Navigation Bar ── */}
      <nav className="hp-navbar">
        <div className="hp-navbar-inner">
          <div className="hp-brand">
            <div className="hp-brand-logo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                <polyline points="14 2 14 8 20 8"/>
                <path d="M12 18v-6"/>
                <path d="M9 15l3-3 3 3"/>
              </svg>
            </div>
            <span className="hp-brand-name">ConvertPro</span>
          </div>

          <div className="hp-nav-links">
            <a href="/pricing" className="hp-nav-link">Pricing</a>
            <a href="/about" className="hp-nav-link">About</a>
            <a href="/feedback" className="hp-nav-link">Feedback</a>
          </div>

          <div className="hp-nav-actions">
            <a href="/login" className="hp-btn-signin">Sign In</a>
            <a href="/signup" className="hp-btn-getstarted">Get Started Free</a>
          </div>
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="hp-hero">
        <div className="hp-hero-content">
          <div className="hp-hero-badge">
            <span>⚡ SECURE, HIGH-SPEED FILE CONVERSION</span>
          </div>
          <h1 className="hp-hero-title">Convert Any File in Seconds</h1>
          <p className="hp-hero-subtitle">
            Experience blazing-fast conversions with zero loss in quality. Supporting over 200+ formats securely in the cloud.
          </p>
        </div>

        {/* Hidden file input shared by both states */}
        <input
          type="file"
          ref={fileInputRef}
          hidden
          onChange={handleFileSelect}
        />

        {!selectedFile ? (
          /* ── Upload Zone (no file selected) ── */
          <div
            className={`hp-upload-zone ${dragActive ? "hp-upload-active" : ""}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="hp-upload-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            <div className="hp-upload-text">
              <h3>Drag & drop your files here or click to browse</h3>
              <p>Supports PDF, DOCX, MP4, PNG, ZIP, CSV, and 200+ other formats up to 100MB free.</p>
            </div>
            <div className="hp-format-badges">
              {["PDF", "DOCX", "MP4", "PNG", "ZIP", "CSV"].map((fmt) => (
                <span key={fmt} className="hp-format-badge">{fmt}</span>
              ))}
            </div>
          </div>
        ) : (
          /* ── Conversion Zone (file selected) ── */
          <div className="hp-convert-zone">
            {/* Source File */}
            <div className="hp-convert-file">
              <div className="hp-convert-file-icon">
                <FileTypeIcon type={getFileIcon(getFileExtension(selectedFile.name))} />
              </div>
              <div className="hp-convert-file-info">
                <span className="hp-convert-filename">{selectedFile.name}</span>
                <span className="hp-convert-filesize">{formatFileSize(selectedFile.size)}</span>
              </div>
              <span className="hp-convert-ext">{getFileExtension(selectedFile.name)}</span>
              <button
                type="button"
                className="hp-convert-remove"
                onClick={handleRemoveFile}
                aria-label="Remove file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Arrow */}
            <div className="hp-convert-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>

            {/* Target Format Selector */}
            <div className="hp-convert-target">
              <button
                type="button"
                className={`hp-convert-target-btn ${targetFormat ? "hp-has-format" : ""}`}
                onClick={() => setShowFormatDropdown(!showFormatDropdown)}
              >
                {targetFormat ? (
                  <>
                    <span className="hp-convert-target-ext">{targetFormat}</span>
                    <span className="hp-convert-target-label">Target format</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                    <span>Select target format</span>
                  </>
                )}
                <svg className="hp-convert-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {showFormatDropdown && (
                <div className="hp-format-dropdown">
                  {Object.entries(formatOptions).map(([category, formats]) => (
                    <div key={category} className="hp-format-dropdown-group">
                      <span className="hp-format-dropdown-label">{category}</span>
                      <div className="hp-format-dropdown-options">
                        {formats.map((fmt) => (
                          <button
                            key={fmt}
                            type="button"
                            className={`hp-format-dropdown-option ${
                              targetFormat === fmt ? "hp-selected" : ""
                            } ${specialFormats.has(fmt) ? "hp-special" : ""}`}
                            onClick={() => {
                              setTargetFormat(fmt);
                              setShowFormatDropdown(false);
                            }}
                          >
                            {fmt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="hp-hero-cta-wrap">
          <button
            className={`hp-hero-cta ${selectedFile && !targetFormat ? "hp-cta-waiting" : ""}`}
            onClick={
              selectedFile && targetFormat
                ? handleConvert
                : !selectedFile
                ? () => fileInputRef.current?.click()
                : undefined
            }
            disabled={
              isConverting ||
              (selectedFile && !targetFormat)
            }
          >
            {isConverting
              ? "Converting..."
              : !selectedFile
              ? "Convert Now — No Sign Up Required"
              : targetFormat
              ? `Convert to ${targetFormat}`
              : "Select a target format above"}
          </button>
          <p className="hp-hero-terms">By converting, you agree to our Terms of Service.</p>
          {downloadUrl && (
            <>
              <p
                style={{
                  color: "#22c55e",
                  marginTop: "12px"
                }}
              >
                Conversion completed successfully!
              </p>

              <div
                style={{
                  marginTop: "8px"
                }}
              >
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#67E8F9",
                    fontSize: "18px",
                    fontWeight: "600",
                    textDecoration: "none",
                    padding: "10px 18px",
                    borderRadius: "10px",
                    background: "rgba(103,232,249,0.12)",
                    border: "1px solid rgba(103,232,249,0.3)",
                    transition: "all 0.2s ease"
                  }}
                >
                  Download Converted File
                </a>
              </div>
            </>
          )}
                    {error && (
                      <p
                        style={{
                          color: "#ef4444",
                          marginTop: "12px"
                        }}
                      >
                        {error}
                      </p>
                    )}
        </div>
      </section>

      {/* ── Trusted By ── */}
      <section className="hp-trusted">
        <p className="hp-trusted-label">TRUSTED BY MODERN TEAMS WORLDWIDE</p>
        <div className="hp-trusted-logos">
          {["Dropbox", "Notion", "Slack", "Vercel", "Stripe", "Linear"].map((name) => (
            <span key={name} className="hp-trusted-logo">{name}</span>
          ))}
        </div>
      </section>

      {/* ── Supported Formats ── */}
      <section className="hp-formats">
        <div className="hp-section-header">
          <span className="hp-section-tag">EXTENSIVE COVERAGE</span>
          <h2 className="hp-section-title hp-white">200+ Formats Supported</h2>
          <p className="hp-section-desc hp-muted">
            Whether documents, ultra-high-res media, or structured development datasets — we have you covered.
          </p>
        </div>

        <div className="hp-format-grid">
          <div className="hp-format-grid-top">
            <FormatCard icon="file-text" title="Documents" formats={["PDF", "DOCX", "TXT", "XLSX", "ODT", "EPUB", "RTF", "HTML", "PPTX" , "PPT" , "DOC" , "XLS" , "ODS" , "CSV" , "MD" , "LATEX" , "TEX"]} />
            <FormatCard icon="image" title="Images" formats={["PNG", "JPG", "SVG", "WEBP", "GIF", "HEIC", "TIFF", "BMP", "ICO" , "AVIF" , "RAW" , "CR2" , "CR3" , "NEF" , "ARW" , "PSD" , "AI" , "EPS"]} />
            <FormatCard icon="video" title="Video" formats={["MP4", "AVI", "MOV", "MKV", "WEBM", "FLV", "WMV", "3GP" , "MPEG" , "MPG" , "M4V" , "TS" , "VOB" , "OGV" , "ASF"]} />
          </div>
          <div className="hp-format-grid-bottom">
            <FormatCard icon="music" title="Audio" formats={["MP3", "WAV", "FLAC", "AAC", "OGG", "M4A", "WMA", "AIFF" , "AMR" , "OPUS" , "MID" , "MIDI" , "AC3" , "APE" , "AU"]} />
            <FormatCard icon="archive" title="Archives" formats={["ZIP", "RAR", "7Z", "TAR", "GZ", "BZ2" , "XZ" , "CAB" , "ISO" , "TGZ" , "TAR.GZ"]} />
            <FormatCard icon="database" title="Data" formats={["CSV", "JSON", "XML", "YAML", "SQL", "TSV", "NDJSON" , "JSONL" , "TOML" , "INI" , "PROTOBUF","GraphQL"]} />
          </div>
          <div className="hp-format-grid-bottom">
              <FormatCard icon="file-text-hl7" title="Healthcare Docs" formats={["HL7 v2.x","HL7 v3","CCD","CDA","FHIR", "X12 270" , "X12 271" , "X12 276" , "X12 277" , "X12 278" , "X12 835" , "X12 837" , "DICOM"]} />
              <FormatCard icon="file-text-edi" title="EDI Docs" formats={["850 Purchase Order" , "810 Invoice" , "856 ASN" , "855 PO Acknowledgement" , "820 Payment" , "846 Inventory" , "940 Warehouse" , "945 Shipment" , "997 Acknowledgement"]} />
              <FormatCard icon="file-text-hadoop" title="Hadoop Docs" formats={["Parquet","Avro","ORC","RC", "Delta" , "Iceberg" , "Hudi" , "Arrow" , "Feather"]} />
          </div>
          <div className="hp-format-grid-bottom">
              <FormatCard icon="file-text-gis" title="GIS/Mapping Docs" formats={["GeoJSON" , "KML" , "GPX" , "Shapefile" , "TopoJSON"]} />
              <FormatCard icon="file-text-sci" title="Scientific Docs" formats={["HDF5" , "NetCDF" , "MAT" , "NPY" , "NPZ"]} />
              <FormatCard icon="file-text-cad" title="CAD/3D Docs" formats={["STL" , "OBJ" , "FBX" , "GLTF" , "GLB" , "STEP" , "DWG" , "DXF"]} />
          </div>
          <div className="hp-format-grid-bottom">
              <FormatCard icon="file-text-sec" title="Security Docs" formats={["PEM" , "CRT" , "CER" , "PFX" , "P12" , "JKS"]} />
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="hp-features">
        <div className="hp-section-header">
          <span className="hp-section-tag">PRODUCT HIGHLIGHTS</span>
          <h2 className="hp-section-title">Built for Professionals</h2>
          <p className="hp-section-desc">
            Everything you expect from an industry-grade media and file integration partner.
          </p>
        </div>

        <div className="hp-feature-grid">
          <FeatureCard
            icon="zap"
            badge="99.9% Uptime"
            title="Lightning Fast"
            desc="Average conversions take under 142ms. Optimized server distribution handles intensive queue loads."
          />
          <FeatureCard
            icon="shield"
            badge="SOC-2 Compliant"
            title="Bank-Grade Security"
            desc="Your uploads are locked down via end-to-end SSL encryption and scrubbed permanently after processing."
          />
          <FeatureCard
            icon="layers"
            badge="Time Saver"
            title="Batch Processing"
            desc="Upload up to 50 files simultaneously. Multithreaded systems convert everything concurrently."
          />
          <FeatureCard
            icon="cpu"
            badge="REST & SDKs"
            title="Robust API Access"
            desc="Developers can integrate file translation seamlessly with simple REST webhooks and 100 req/sec limit."
          />
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="hp-footer">
        <div className="hp-footer-inner">
          <div className="hp-footer-top">
            <div className="hp-footer-brand">
              <div className="hp-brand hp-brand-footer">
                <div className="hp-brand-logo hp-brand-logo-sm">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <path d="M12 18v-6"/>
                    <path d="M9 15l3-3 3 3"/>
                  </svg>
                </div>
                <span className="hp-brand-name">ConvertPro</span>
              </div>
              <p className="hp-footer-tagline">
                Secure, lightning-fast file conversions directly in your browser. Engineered for professionals and creators worldwide.
              </p>
              <div className="hp-social-icons">
                <SocialIcon icon="twitter" />
                <SocialIcon icon="github" />
                <SocialIcon icon="linkedin" />
                <SocialIcon icon="facebook" />
              </div>
            </div>

            <div className="hp-footer-col">
              <h4>Product</h4>
              <button type="button" className="hp-footer-link">Features</button>
              <a href="/pricing" className="hp-footer-link">Pricing</a>
              <button type="button" className="hp-footer-link">API Specs</button>
              <button type="button" className="hp-footer-link">Integrations</button>
            </div>
            <div className="hp-footer-col">
              <h4>Company</h4>
              <a href="/about" className="hp-footer-link">About</a>
              <button type="button" className="hp-footer-link">Blog</button>
              <button type="button" className="hp-footer-link">Careers</button>
              <button type="button" className="hp-footer-link">Press Kit</button>
            </div>
            <div className="hp-footer-col">
              <h4>Resources</h4>
              <button type="button" className="hp-footer-link">Documentation</button>
              <button type="button" className="hp-footer-link">Help Center</button>
              <button type="button" className="hp-footer-link">Status</button>
              <button type="button" className="hp-footer-link">Contact</button>
            </div>
            <div className="hp-footer-col">
              <h4>Legal</h4>
              <button type="button" className="hp-footer-link">Terms of Use</button>
              <button type="button" className="hp-footer-link">Privacy Policy</button>
              <button type="button" className="hp-footer-link">SLA Guarantees</button>
              <button type="button" className="hp-footer-link">HIPAA Compliance</button>
            </div>
          </div>

          <div className="hp-footer-divider" />
          <div className="hp-footer-bottom">
            <p>&copy; 2026 ConvertPro Systems Inc. All rights reserved.</p>
            <div className="hp-footer-bottom-links">
              <button type="button" className="hp-footer-link">Security Policy</button>
              <button type="button" className="hp-footer-link">Cookie Choices</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ── Sub-components ── */

const FormatCard = ({ icon, title, formats }) => (
  <div className="hp-format-card">
    <div className="hp-format-card-header">
      <div className="hp-format-icon-wrap">
        <FormatIcon name={icon} />
      </div>
      <h3>{title}</h3>
    </div>
    <div className="hp-format-card-tags">
      {formats.map((f) => (
        <span
          key={f}
          className={`hp-format-tag ${specialFormats.has(f) ? "hp-special" : ""}`}
        >
          {f}
        </span>
      ))}
    </div>
  </div>
);

const FeatureCard = ({ icon, badge, title, desc }) => (
  <div className="hp-feature-card">
    <div className="hp-feature-card-header">
      <div className="hp-feature-icon-wrap">
        <FeatureIcon name={icon} />
      </div>
      <span className="hp-feature-badge">{badge}</span>
    </div>
    <div className="hp-feature-card-body">
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  </div>
);

const SocialIcon = ({ icon }) => {
  const paths = {
    twitter: <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53A4.48 4.48 0 0 0 22.43.36a9 9 0 0 1-2.88 1.1A4.52 4.52 0 0 0 12 4.77v1A10.66 10.66 0 0 1 3 3.66s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />,
    github: <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />,
    linkedin: <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></>,
    facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
  };
  return (
    <button type="button" className="hp-social-btn" aria-label={icon}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {paths[icon]}
      </svg>
    </button>
  );
};

const FileTypeIcon = ({ type }) => {
  const icons = {
    "file-text": <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></>,
    video: <><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></>,
    music: <><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></>,
    file: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></>,
  };
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[type] || icons.file}
    </svg>
  );
};

const FeatureIcon = ({ name }) => {
  const icons = {
    zap: <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
    cpu: <><rect x="4" y="4" width="16" height="16" rx="2" ry="2" /><rect x="9" y="9" width="6" height="6" /><line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" /><line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" /><line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" /><line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" /></>,
  };
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

const FormatIcon = ({ name }) => {
  const icons = {
    "file-text": <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>,
    video: <><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></>,
    music: <><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>,
    archive: <><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></>,
    database: <><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></>,
  };
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

export default ConvertProHomepage;